import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const EMPTY_POST = {
  slug: "",
  title: "",
  excerpt: "",
  category: "AI Engineering",
  read_time: "5 min read",
  author: "Spin Labs",
  cover_img: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1600&q=75",
  content_text: "",
  featured: false,
  order: 100,
};

export default function AdminPostsPanel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {mode:'create'|'edit', original:string|null, data:{...}}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/posts");
      setPosts(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => setEditing({ mode: "create", original: null, data: { ...EMPTY_POST } });
  const openEdit = (p) => {
    const content_text = (p.content || [])
      .map((b) => (b.type === "h2" ? `## ${b.text}` : b.text))
      .join("\n\n");
    setEditing({
      mode: "edit",
      original: p.slug,
      data: {
        slug: p.slug,
        title: p.title,
        excerpt: p.excerpt,
        category: p.category,
        read_time: p.read_time,
        author: p.author,
        cover_img: p.cover_img,
        content_text,
        featured: !!p.featured,
        order: p.order ?? 100,
      },
    });
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (editing.mode === "create") {
        await api.post("/admin/posts", editing.data);
        toast.success("Post created");
      } else {
        await api.put(`/admin/posts/${editing.original}`, editing.data);
        toast.success("Post updated");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete post '${slug}'? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/posts/${slug}`);
      toast.success("Post deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateField = (k, v) => setEditing((e) => ({ ...e, data: { ...e.data, [k]: v } }));

  return (
    <div data-testid="admin-posts-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-xl font-bold">Blog posts</h3>
          <p className="text-xs text-white/50 font-mono-neo uppercase tracking-[0.2em]">{posts.length} total</p>
        </div>
        <button onClick={openCreate} data-testid="admin-new-post" className="btn-primary !px-5 !py-2.5 !text-sm">
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#0A0A0C] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : posts.length === 0 ? (
          <div className="p-10 text-center text-white/40 text-sm">No posts yet</div>
        ) : (
          <div className="divide-y divide-white/5">
            {posts.map((p) => (
              <div key={p.slug} data-testid={`admin-post-row-${p.slug}`} className="p-5 flex flex-wrap items-center gap-4 hover:bg-white/5 transition-colors">
                <img src={p.cover_img} alt="" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-display font-bold truncate">{p.title}</div>
                  <div className="text-xs text-white/50 truncate">/{p.slug}</div>
                </div>
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]">{p.category}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEdit(p)} className="p-2 rounded-lg border border-white/10 hover:border-[#00F0FF]/50 hover:text-[#00F0FF]" title="Edit"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(p.slug)} data-testid={`admin-post-delete-${p.slug}`} className="p-2 rounded-lg border border-white/10 hover:border-red-500/50 hover:text-red-400" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <EditorModal
          title={editing.mode === "create" ? "New blog post" : `Edit: ${editing.original}`}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
        >
          <TwoCol>
            <Field label="Slug (URL)" required>
              <Input value={editing.data.slug} onChange={(e) => updateField("slug", e.target.value)} placeholder="my-post-slug" className={inputCls} />
            </Field>
            <Field label="Title" required>
              <Input value={editing.data.title} onChange={(e) => updateField("title", e.target.value)} className={inputCls} />
            </Field>
          </TwoCol>
          <Field label="Excerpt" required>
            <Textarea value={editing.data.excerpt} onChange={(e) => updateField("excerpt", e.target.value)} className={`${inputCls} min-h-[80px]`} />
          </Field>
          <TwoCol>
            <Field label="Category" required><Input value={editing.data.category} onChange={(e) => updateField("category", e.target.value)} className={inputCls} /></Field>
            <Field label="Read time" required><Input value={editing.data.read_time} onChange={(e) => updateField("read_time", e.target.value)} className={inputCls} /></Field>
          </TwoCol>
          <TwoCol>
            <Field label="Author" required><Input value={editing.data.author} onChange={(e) => updateField("author", e.target.value)} className={inputCls} /></Field>
            <Field label="Order" hint="Lower = earlier"><Input type="number" value={editing.data.order} onChange={(e) => updateField("order", parseInt(e.target.value) || 100)} className={inputCls} /></Field>
          </TwoCol>
          <Field label="Cover image URL" required>
            <Input value={editing.data.cover_img} onChange={(e) => updateField("cover_img", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Content" hint="Lines starting with '## ' become headings. Blank lines split paragraphs." required>
            <Textarea value={editing.data.content_text} onChange={(e) => updateField("content_text", e.target.value)} className={`${inputCls} min-h-[240px] font-mono-neo text-sm`} placeholder={"Intro paragraph goes here.\n\n## A subsection heading\n\nAnother paragraph."} />
          </Field>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={editing.data.featured} onChange={(e) => updateField("featured", e.target.checked)} className="accent-[#00F0FF]" />
            Featured on Insights page
          </label>
        </EditorModal>
      )}
    </div>
  );
}

// ====== Shared helpers ======
const inputCls = "bg-black/40 border-white/10 text-white focus-visible:ring-[#00F0FF] focus-visible:border-[#00F0FF]";

export function Field({ label, hint, required, children }) {
  return (
    <div>
      <Label className="text-xs font-mono-neo uppercase tracking-[0.2em] text-white/50">
        {label}{required && <span className="text-[#FF00FF]"> *</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="mt-1 text-[11px] text-white/40">{hint}</div>}
    </div>
  );
}

export function TwoCol({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

export function EditorModal({ title, onClose, onSave, saving, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 overflow-auto" data-testid="admin-editor-modal">
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0A0A0C] my-8">
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-white/10 bg-[#0A0A0C] rounded-t-2xl">
          <h3 className="font-display font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center hover:border-[#FF00FF]/50 hover:text-[#FF00FF]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 sm:p-6 space-y-5">
          {children}
        </div>
        <div className="sticky bottom-0 p-5 border-t border-white/10 bg-[#0A0A0C] rounded-b-2xl flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-white/10 text-sm text-white/70 hover:border-white/30">Cancel</button>
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="admin-editor-save"
            className="btn-primary !px-5 !py-2.5 !text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
